import React, { useState, useEffect } from "react";
import {
  Facebook,
  Instagram,
  Twitter,
  Linkedin,
  Mail,
  MapPin,
  Phone,
  Info,
  Briefcase,
  Layers,
  Clock,
  ArrowUp,
  ExternalLink,
  Heart,
} from "lucide-react";
// Subsidiary logos (used on the Subsidiaries page)
import aspenLogo from "../assets/images/logos/aspen-logo-removebg-preview.png";
import exploitsLogo from "../assets/images/logos/expl_university-removebg-preview.png";
import geminiLogo from "../assets/images/logos/Gemini_Generated_Image_thz4hmthz4hmthz4-removebg-preview.png";

export default function Footer({ navigateTo }) {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [currentYear] = useState(new Date().getFullYear());

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 500);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const quickLinks = [
    { icon: <Info size={16} />, label: "About Us", page: "about" },
    { icon: <Briefcase size={16} />, label: "Subsidiaries", page: "subsidiaries" },
    { icon: <Layers size={16} />, label: "Services", page: "services" },
    { icon: <Mail size={16} />, label: "Contact", page: "contact" },
  ];

  const socialLinks = [
    { icon: <Facebook size={20} />, label: "Facebook", url: "https://www.facebook.com/p/Madison-Holdings-100046424636364/", color: "#1877F2" },
    { icon: <Linkedin size={20} />, label: "LinkedIn", url: "https://www.linkedin.com/company/madison-holdings-limited/", color: "#0A66C2" },
  ];

  const subsidiaries = [
    { name: "Aspen", url: "http://aspenmw.com", logo: aspenLogo },
    { name: "Exploits University", url: "https://exploitsmw.com/", logo: exploitsLogo },
    { name: "Riverton Dairy", url: "http://rivertonmw.com", logo: geminiLogo },
  ];

  return (
    <>
      <footer className="footer">
        <div className="footer-container">
          {/* Company Info Section */}
          {/* <div className="footer-section footer-about">
            <h3>Madison Group</h3>
            <p>
              Madison Group is scaling operations to address Malawi's
              accommodation shortages and develop alternative energy solutions,
              driving innovation through workplace diversity and sustainable
              strategies.
            </p>
            <div className="footer-badges">
              <div className="badge">
                <span className="badge-year">Since 2007</span>
              </div>
              <div className="badge">
                <span className="badge-text">7+ Subsidiaries</span>
              </div>
            </div>
          </div> */}

          {/* Contact Section */}
          <div className="footer-section">
            <h3>Contact Us</h3>
            <div className="contact-list">
              <div className="contact-item">
                <MapPin size={16} />
                <span>City Centre, ADL House, P.O. Box 31507, Lilongwe</span>
              </div>
              <a href="mailto:info@madisonmw.com" className="contact-item">
                <Mail size={16} />
                <span>info@madisonmw.com</span>
              </a>
              <a href="tel:+2651775433" className="contact-item">
                <Phone size={16} />
                <span>01 775 433</span>
              </a>
              <a href="tel:+265998955221" className="contact-item">
                <Phone size={16} />
                <span>+265 998 955 221</span>
              </a>
            </div>

            <div className="visit-us-section">
              <h4>
                <Clock size={18} style={{ marginRight: "8px" }} />
                Visit Us
              </h4>
              <div className="visit-hours">
                <Clock size={16} />
                <div className="hours-detail">
                  <span className="days">Monday - Friday</span>
                  <span className="time">8:00 AM - 5:00 PM</span>
                </div>
              </div>
              <div className="visit-hours">
                <Clock size={16} />
                <div className="hours-detail">
                  <span className="days">Weekends and Public Holidays</span>
                  <span className="time">CLOSED</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links Section */}
          <div className="footer-section">
            <h3>Quick Links</h3>
            <div className="footer-links">
              {quickLinks.map((link, index) => (
                <a
                  key={index}
                  onClick={() => navigateTo(link.page)}
                  className="link-item"
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") navigateTo(link.page);
                  }}
                >
                  {link.icon}
                  <span>{link.label}</span>
                </a>
              ))}
            </div>

            <h4 style={{ marginTop: "30px", fontSize: "14px" }}>
              Our Subsidiaries
            </h4>
            <div className="subsidiaries-list">
              {subsidiaries.map((sub, index) => (
                <a
                  key={index}
                  className="subsidiary-tag"
                  href={sub.url || "#"}
                  target={sub.url && sub.url.startsWith("http") ? "_blank" : undefined}
                  rel={sub.url && sub.url.startsWith("http") ? "noopener noreferrer" : undefined}
                >
                  {sub.logo && (
                    <img
                      src={sub.logo}
                      alt={`${sub.name} logo`}
                      className="footer-subsidiary-logo"
                    />
                  )}
                  <span>{sub.name}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Social Media Section */}
          <div className="footer-section">
            <h3>Connect With Us</h3>
            <p style={{ marginBottom: "20px", fontSize: "14px" }}>
              Follow us on social media for updates and news
            </p>
            <div className="footer-social">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                  aria-label={social.label}
                  style={{ "--social-color": social.color }}
                >
                  {social.icon}
                  <span>{social.label}</span>
                  <ExternalLink size={14} className="external-icon" />
                </a>
              ))}

              {/* Newsletter Signup */}
              {/* <div className="newsletter-section">
                <h4>Stay Updated</h4>
                <p>Subscribe to our newsletter</p>
                <div className="newsletter-form">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    aria-label="Email for newsletter"
                  />
                  <button type="button" aria-label="Subscribe to newsletter">
                    <Mail size={16} />
                  </button>
                </div>
              </div> */}
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="footer-bottom">
          <div className="footer-bottom-content">
            <p>&copy; {currentYear} Madison Holdings Limited. All rights reserved.</p>
            <p className="footer-tagline">
              Made with <Heart size={14} className="heart-icon" /> in Malawi
            </p>
            <div className="footer-legal">
              <a href="#" onClick={(e) => e.preventDefault()}>
                Privacy Policy
              </a>
              <span>•</span>
              <a href="#" onClick={(e) => e.preventDefault()}>
                Terms of Service
              </a>
              <span>•</span>
              <a href="#" onClick={(e) => e.preventDefault()}>
                Cookie Policy
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      <button
        className={`scroll-to-top ${showScrollTop ? "visible" : ""}`}
        onClick={scrollToTop}
        aria-label="Scroll to top"
      >
        <ArrowUp size={20} />
      </button>
    </>
  );
}
